<template>
  <div class="p-6">
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
      <router-link
        :to="{ name: 'invite-members', params: { vault_id: $route.params.vault_id } }"
        class="inline-flex items-center justify-center bg-brand border border-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-dark hover:border-brand-dark no-underline"
        v-is-authorized="{ roles: [role], permissions: ['can_add_members'] }"
      >
        <i class="fa fa-user-plus mr-2"></i> Add member
      </router-link>
      <input
        type="text"
        class="h-10 border rounded px-3 w-full md:w-64 mt-2 md:mt-0"
        placeholder="Search by name"
        v-model="filters.name"
        @input="filterUsers(1)"
      />
    </div>

    <div v-if="loading" class="w-full h-64 bg-grey-light flex justify-center items-center">
      <span><i class="fa fa-spin fa-circle-notch"></i> Loading members. Please wait.</span>
    </div>

    <template v-else-if="members.data && members.data.length > 0">
      <!-- Desktop: a table so columns size to their content -->
      <div class="hidden md:block overflow-x-auto">
        <table class="min-w-full table-auto">
          <thead>
            <tr class="border-b text-left text-xxs uppercase tracking-wide text-grey-dark font-semibold">
              <th class="px-3 py-3 font-semibold">Name</th>
              <th class="px-3 py-3 font-semibold">Email</th>
              <!-- fixed width so the row does not jump when the role editor opens -->
              <th class="px-3 py-3 font-semibold w-64">Role</th>
              <th class="px-3 py-3 font-semibold">Company</th>
              <th class="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(member, index) in members.data"
              :key="member.id"
              class="border-b align-middle"
              :class="{ 'bg-brand-lightest': index % 2 !== 0 }"
            >
              <td class="px-3 py-2 whitespace-no-wrap font-medium">{{ fullName(member) }}</td>
              <td class="px-3 py-2 break-all">{{ member.email }}</td>
              <td class="px-3 py-2 w-64">
                <member-role
                  :member="member"
                  :role="role"
                  :roles="roles"
                  :user="user"
                  @role-updated="getVaultMembers"
                ></member-role>
              </td>
              <td class="px-3 py-2">
                <span v-if="member.company_name">{{ member.company_name }}</span>
                <span v-else class="text-grey">-</span>
              </td>
              <td class="px-3 py-2 whitespace-no-wrap text-right">
                <button
                  type="button"
                  class="border border-red bg-white text-red text-sm font-medium py-2 px-4 rounded hover:bg-red hover:text-white"
                  v-if="canBeDeleted(member)"
                  v-is-authorized="{ roles: [role], permissions: ['can_add_members'] }"
                  @click.prevent="forDeletion(member)"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile: one stacked card per member -->
      <div class="md:hidden">
        <div
          v-for="(member, index) in members.data"
          :key="member.id"
          class="px-3 py-3 border-b"
          :class="{ 'bg-brand-lightest': index % 2 !== 0 }"
        >
          <p class="font-medium">{{ fullName(member) }}</p>
          <p class="break-all text-sm text-grey-darker">{{ member.email }}</p>
          <div class="flex flex-wrap text-sm mt-2">
            <div class="w-full py-1">
              <span class="text-xs font-semibold text-grey-darker block">Role</span>
              <member-role
                :member="member"
                :role="role"
                :roles="roles"
                :user="user"
                @role-updated="getVaultMembers"
              ></member-role>
            </div>
            <div class="w-full py-1" v-if="member.company_name">
              <span class="text-xs font-semibold text-grey-darker block">Company</span>
              {{ member.company_name }}
            </div>
          </div>
          <button
            type="button"
            class="w-full mt-2 border border-red bg-white text-red text-sm font-medium py-2 px-4 rounded"
            v-if="canBeDeleted(member)"
            v-is-authorized="{ roles: [role], permissions: ['can_add_members'] }"
            @click.prevent="forDeletion(member)"
          >
            Delete
          </button>
        </div>
      </div>

      <div class="py-4 flex justify-center items-center text-sm text-grey-darker" v-if="hasPages(members)">
        <button
          type="button"
          class="border border-grey bg-white text-grey-darker text-sm font-medium py-2 px-4 rounded mx-1 hover:bg-grey-lightest"
          :class="{ 'opacity-50 cursor-not-allowed': members.current_page == 1 }"
          :disabled="members.current_page == 1"
          @click.prevent="filterUsers(members.current_page - 1)"
        >
          <i class="fa fa-angle-left"></i> Previous
        </button>
        <span class="mx-3">{{ pagerLabel(members) }}</span>
        <button
          type="button"
          class="border border-grey bg-white text-grey-darker text-sm font-medium py-2 px-4 rounded mx-1 hover:bg-grey-lightest"
          :class="{ 'opacity-50 cursor-not-allowed': members.current_page == members.last_page }"
          :disabled="members.current_page == members.last_page"
          @click.prevent="filterUsers(members.current_page + 1)"
        >
          Next <i class="fa fa-angle-right"></i>
        </button>
      </div>
    </template>

    <div v-else class="w-full h-64 bg-grey-light flex flex-col justify-center items-center">
      <p class="text-grey-darker" v-if="filters.name">No members match "{{ filters.name }}".</p>
      <p class="text-grey-darker" v-else>This vault has no members yet.</p>
    </div>

    <confirm-dialog
      ref="member_delete"
      title="Delete this member?"
      :body="deleteBody"
      action-label="Delete"
      @confirm="confirmDelete"
    ></confirm-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ConfirmDialog from "../../components/ConfirmDialog.vue";
import MemberRole from "../../components/ManageMembers/MemberRole.vue";
import { hasPages, pagerLabel } from "../../dashboard_pager.mjs";

export default {
  components: { ConfirmDialog, MemberRole },

  data() {
    return {
      members: {},
      for_deletion: {},
      loading: true,

      filters: {
        role_id: "",
        name: "",
      },

      roles: {},

      custom_header: {
        title: ["User A"],
      },
    };
  },

  mounted() {
    this.getVaultMembers();
    this.getRoles();
  },

  methods: {
    hasPages,
    pagerLabel,

    getVaultMembers() {
      this.loading = true;
      axios
        .get(`/api/vaults/${this.$route.params.vault_id}/users`)
        .then(({ data }) => {
          this.members = data;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getRoles() {
      axios.get(`api/vaults/${this.$route.params.vault_id}/roles`).then(({ data }) => {
        let cleaned_roles = [];
        _.each(data, (role) => {
          let Role = this.getRoleName(role);

          if (!_.includes(["User A", "User E"], _.trim(Role))) {
            cleaned_roles.push(role);
          }
        });

        if (window.vault_type == "business") {
          _.each(cleaned_roles, (datum, key) => {
            cleaned_roles[key]["role"] = this.sanitize(datum);
          });
        }

        this.roles = cleaned_roles;
      });
    },

    getRoleName(role) {
      let vault_id = this.$route.params.vault_id;
      let role_name = role.name;
      let replace_char = vault_id + "-";
      return role_name.replace(replace_char, " ");
    },

    sanitize(role) {
      let Role = this.getRoleName(role);

      let index = _.findIndex(this.custom_header.title, (t) => {
        return _.trim(t) == _.trim(Role);
      });

      if (index == -1) {
        return Role;
      } else {
        return role.title.replace(/.+?(\- )/g, "");
      }
    },

    fullName(member) {
      return `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim() || "-";
    },

    // the member's own role token, e.g. "userA"
    memberRole(member) {
      let vault_id = this.$route.params.vault_id;
      let role_name = member.roles[0].name;
      let replace_char = vault_id + "-";

      return _.camelCase(role_name.replace(replace_char, ""));
    },

    canBeDeleted(member) {
      if (this.role == "userB" && this.memberRole(member) == "userA") {
        return false;
      }

      return member.id != this.user.id;
    },

    filterUsers: _.debounce(function (page) {
      this.loading = true;
      axios
        .get(`/api/vaults/${this.$route.params.vault_id}/users`, {
          params: {
            name: this.filters.name,
            role_id: this.filters.role_id,
            page: page,
          },
        })
        .then(({ data }) => {
          this.members = data;
        })
        .finally(() => {
          this.loading = false;
        });
    }, 500),

    forDeletion(member) {
      this.for_deletion = member;
      this.$refs.member_delete.open();
    },

    confirmDelete() {
      axios
        .delete(`/api/vaults/${this.$route.params.vault_id}/users/${this.for_deletion.id}/delete`)
        .then(({ data }) => {
          if (data.success) {
            this.$noty.success(data.message || "Member deleted", { killer: true, timeout: 3000 });
            this.getVaultMembers();
          } else {
            this.$noty.warning(data.message || "The member could not be deleted", { killer: true, timeout: 4000 });
          }
        })
        .catch((error) => {
          const message = error.response && error.response.data && error.response.data.message;
          this.$noty.error(message || "The member could not be deleted", { killer: true, timeout: 4000 });
        })
        .finally(() => {
          this.for_deletion = {};
        });
    },
  },

  computed: {
    ...mapGetters({
      user: "getUser",
      role: "getUserRole",
    }),

    deleteBody() {
      const name = this.for_deletion.id ? this.fullName(this.for_deletion) : "This member";
      return `${name} will no longer have access to this vault.`;
    },
  },
};
</script>
